import React, { useEffect, useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { setCurrentPage } from "../../../../../Redux/slice/features/paginationSilce";
import { REACT_APP_API_URL } from "../../../../../env";
import {
  fetchUserDetails,
  rehydrateAuthState,
} from "../../../../../Redux/slice/features/authSlice";

const checkInStyles = `
  .checkin-wrapper {
    background: #ffffff;
    border-radius: 16px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.03);
    overflow: hidden;
    margin-top: 1.5rem;
    border: 1px solid #f1f5f9;
    font-family: 'Inter', system-ui, -apple-system, sans-serif;
  }
  .checkin-top-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 24px;
    background: #ffffff;
    border-bottom: 1px solid #eaedf0;
  }
  .checkin-badge {
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
    color: white;
    padding: 8px 18px;
    border-radius: 20px;
    font-weight: 600;
    font-size: 0.85rem;
    letter-spacing: 0.02em;
    box-shadow: 0 4px 12px rgba(37, 99, 235, 0.2);
  }
  .checkin-table-container {
    overflow-x: auto;
    width: 100%;
  }
  .checkin-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    min-width: 1100px;
    text-align: left;
  }
  .checkin-table th {
    background: #f8fafc;
    color: #475569;
    font-weight: 600;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    padding: 18px 24px;
    border-bottom: 2px solid #e2e8f0;
    white-space: nowrap;
  }
  .checkin-table td {
    padding: 18px 24px;
    vertical-align: middle;
    border-bottom: 1px solid #f1f5f9;
    color: #1e293b;
    font-size: 0.875rem;
    font-weight: 500;
  }
  .checkin-table tbody tr {
    transition: all 0.2s ease;
  }
  .checkin-table tbody tr:hover {
    background-color: #f8fafc;
  }
  .booking-id-link {
    color: #2563eb;
    font-weight: 700;
    text-decoration: none;
    background: #eff6ff;
    padding: 6px 12px;
    border-radius: 8px;
    font-size: 0.8rem;
    cursor: pointer;
    transition: all 0.2s;
    border: 1px solid #dbeafe;
  }
  .booking-id-link:hover {
    background: #dbeafe;
    color: #1d4ed8;
    transform: translateY(-1px);
  }
  .customer-info {
    display: flex;
    flex-direction: column;
  }
  .customer-name {
    font-weight: 700;
    color: #0f172a;
  }
  .customer-phone {
    font-size: 0.75rem;
    color: #64748b;
    margin-top: 2px;
  }
  .val-badge {
    padding: 6px 12px;
    border-radius: 8px;
    font-weight: 600;
    font-size: 0.75rem;
  }
  .badge-rooms { background: #fef3c7; color: #92400e; }
  .badge-amount { background: #d1fae5; color: #065f46; }
  .badge-date { background: #f1f5f9; color: #475569; border-radius: 6px; padding: 4px 8px; font-size: 0.8rem; }
  
  .pagination-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 24px;
    background: #f8fafc;
    border-top: 1px solid #eaedf0;
  }
  .btn-page {
    background: #ffffff;
    border: 1px solid #cbd5e1;
    color: #334155;
    padding: 8px 18px;
    border-radius: 8px;
    font-weight: 600;
    font-size: 0.875rem;
    cursor: pointer;
    transition: all 0.2s;
  }
  .btn-page:hover:not(:disabled) {
    background: #f1f5f9;
    border-color: #94a3b8;
  }
  .btn-page:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .loader-td { text-align: center; padding: 60px; color: #94a3b8; }
`;

const TodayCheckInTable = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { currentPage, itemsPerPage } = useSelector(
    (state) => state.pagination
  );
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const userHotelId = user?.user?.PropertyID;

  useEffect(() => {
    dispatch(rehydrateAuthState());
    dispatch(fetchUserDetails());
  }, [dispatch]);

  useEffect(() => {
    dispatch(setCurrentPage(1));
  }, [dispatch]);

  const fetchReport = useCallback(async () => {
    const HotelID = userHotelId ?? 0;
    setLoading(true);
    try {
      const res = await axios.post(
        `${REACT_APP_API_URL}api/dashboard/admin/today-checkin`,
        {
          hotelId: HotelID,
          pageNo: currentPage,
          pageSize: itemsPerPage,
          checkinDate: new Date().toISOString().split('T')[0],
        }
      );
      
      const formatted = (res.data.data || []).map((item) => ({
        BookingID: item.BookingID || "-",
        PropertyName: item.PropertyName || "-",
        CustomerName: item.CustomerName || "-",
        CheckinDate: item.CheckinDate || "-",
        CheckoutDate: item.CheckOutDate || "-",
        BookingRefNo: item.BookingRefNo || "-",
        NoOfRooms: item.TotalNoOfRoom || 0,
        TotalAmount: item.TotalAmount || 0,
        MobileNo: item.MobileNo || "-",
      }));

      setData(formatted);
    } catch (err) {
      console.error("Error fetching report:", err);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [userHotelId, currentPage, itemsPerPage]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleBookingClick = (bookingId) => {
    if (!bookingId || bookingId === "-") return;
    window.open(`/booking-view/${bookingId}`, "_blank", "width=800,height=600");
  };

  const handleNextPage = () => dispatch(setCurrentPage(currentPage + 1));
  const handlePreviousPage = () => dispatch(setCurrentPage(currentPage - 1));

  const totalPages = Math.max(1, Math.ceil(data.length / itemsPerPage));

  return (
    <>
      <style>{checkInStyles}</style>
      <div className="checkin-wrapper">
        <div className="checkin-top-bar">
          <div className="checkin-badge">Today's Check-ins</div>
          <div style={{ fontSize: '0.9rem', color: '#64748b' }}>
            Total Arrivals: <span style={{ fontWeight: 700, color: '#0f172a' }}>{data.length}</span>
          </div>
        </div>

        <div className="checkin-table-container">
          <table className="checkin-table">
            <thead>
              <tr>
                <th>Booking ID</th>
                <th>Booking Ref</th>
                <th>Customer Details</th>
                <th>Check-in / Out</th>
                <th>Rooms</th>
                <th>Amount</th>
                <th>Hotel</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="loader-td">Retrieving today's arrivals...</td>
                </tr>
              ) : data.length > 0 ? (
                data.map((item, idx) => (
                  <tr key={idx}>
                    <td>
                      <span className="booking-id-link" onClick={() => handleBookingClick(item.BookingID)}>
                        #{item.BookingID}
                      </span>
                    </td>
                    <td style={{ fontFamily: 'monospace', color: '#64748b' }}>{item.BookingRefNo}</td>
                    <td>
                      <div className="customer-info">
                        <span className="customer-name">{item.CustomerName}</span>
                        <span className="customer-phone">{item.MobileNo}</span>
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <span className="badge-date">In: {item.CheckinDate}</span>
                        <span className="badge-date">Out: {item.CheckoutDate}</span>
                      </div>
                    </td>
                    <td>
                      <span className="val-badge badge-rooms">{item.NoOfRooms} Rooms</span>
                    </td>
                    <td>
                      <span className="val-badge badge-amount">₹{Number(item.TotalAmount).toLocaleString('en-IN')}</span>
                    </td>
                    <td style={{ fontSize: '0.8rem', color: '#475569' }}>{item.PropertyName}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="loader-td">No check-ins scheduled for today.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="pagination-row">
          <div style={{ fontSize: '0.875rem', color: '#475569' }}>
            Page <b>{currentPage}</b> of <b>{totalPages}</b>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button className="btn-page" onClick={handlePreviousPage} disabled={currentPage === 1}>Previous</button>
            <button className="btn-page" onClick={handleNextPage} disabled={data.length < itemsPerPage}>Next</button>
          </div>
        </div>
      </div>
    </>
  );
};

export default TodayCheckInTable;
